import { useCallback, useEffect, useRef, useState } from 'react';
import { PREVIEW_URL } from '../config.js';

// Rendered at a display's size and scaled down, rather than letting the app
// reflow into a small box - the point of a preview is to show what a display
// actually shows, and the shelf lays out very differently at 400px.
//
// Portrait, because that is how these displays are mounted.
const PREVIEW_WIDTH = 720;
const PREVIEW_HEIGHT = 1280;

// The conventional arrow-out-of-a-box, so the link reads as leaving this page
// before it is clicked. Decorative: the adjacent text carries the meaning.
function ExternalLinkIcon() {
  return (
    <svg className="external-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M14 4h6v6" />
      <path d="M20 4l-8.5 8.5" />
      <path d="M18 14.5V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4.5" />
    </svg>
  );
}

export default function PreviewPanel() {
  const stageRef = useRef(null);
  const [scale, setScale] = useState(1);
  // Changing the key remounts the iframe, which is the only reliable way to
  // reload a cross-origin document we cannot reach into.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      setScale(entry.contentRect.width / PREVIEW_WIDTH);
    });
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  return (
    <section className="panel">
      <div className="panel-header">
        <p className="panel-hint">
          What a paired display is showing right now, at {PREVIEW_WIDTH}×{PREVIEW_HEIGHT}.
        </p>
        <div className="panel-actions">
          <button type="button" onClick={reload}>Reload</button>
          <a href={PREVIEW_URL} target="_blank" rel="noreferrer">
            Preview
            <ExternalLinkIcon />
          </a>
        </div>
      </div>

      <div className="preview-stage" ref={stageRef}>
        <iframe
          key={reloadKey}
          className="preview-frame"
          src={PREVIEW_URL}
          title="Display preview"
          style={{
            width: `${PREVIEW_WIDTH}px`,
            height: `${PREVIEW_HEIGHT}px`,
            transform: `scale(${scale})`,
          }}
        />
      </div>
    </section>
  );
}
